import { useState } from 'react';
import { GROUPABLE_FIELDS, PRIORITY_LABEL, type ViewConfig } from '@planning/contracts';
import { SelectorMultiple, SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Label } from '@xiltrion/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@xiltrion/ui/popover';
import { Switch } from '@xiltrion/ui/switch';
import { Badge } from '@xiltrion/ui/badge';
import { apiPatch } from '@/api/client';
import { avisarError, usePersonas, useTeams } from '@/api/hooks';
import type { ProjectDetail, View } from '@/api/types';
import { useSession, useTerms } from '@/app/session';

const GROUP_LABEL: Record<string, string> = { section: 'Sección', priority: 'Prioridad', assignee: 'Responsable', label: 'Etiqueta', completed: 'Estado', due: 'Fecha', project: 'Proyecto' };

/** Filtros, orden y agrupación de la vista; se guardan en la vista (compartida o personal). */
export function ViewFilters({ view, project, editable, onSaved }: { view: View; project: ProjectDetail | null; editable: boolean; onSaved: () => void }) {
  const { me } = useSession();
  const { personas } = usePersonas();
  const { allOptions: teamOptions } = useTeams();
  const { t } = useTerms();
  const [open, setOpen] = useState(false);
  const cfg = view.config;
  const save = (patch: Partial<ViewConfig>) => void apiPatch(`/views/${view.id}`, { config: { ...cfg, ...patch } }).then(onSaved, avisarError);
  const filterValue = (field: string) => (cfg.filters.find((f) => f.field === field)?.value as string[] | undefined) ?? [];
  const setFilter = (field: string, values: string[]) => save({ filters: [...cfg.filters.filter((f) => f.field !== field), ...(values.length ? [{ field, operator: 'in' as const, value: values }] : [])] });
  const active = cfg.filters.length + (cfg.showCompleted === false ? 1 : 0);
  const groupOptions = (project ? [...GROUPABLE_FIELDS] : ['due', 'project', 'team', 'priority', 'completed']).map((g) => ({ value: g, label: g === 'team' ? t('team') : GROUP_LABEL[g] ?? g }));
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8"><Icono nombre="mdi:filter-variant" />Filtros{active > 0 && <Badge size="sm" variant="primary-light">{active}</Badge>}</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-3">
        {!editable && <p className="text-xs text-muted-foreground">Esta vista es del equipo: tus cambios no se guardan. Crea una vista personal para guardarlos.</p>}
        <div className="space-y-1.5">
          <Label>Agrupar por</Label>
          <SelectorSimple size="sm" value={cfg.groupBy ?? (project ? 'section' : 'due')} onChange={(v) => editable && save({ groupBy: v })} options={groupOptions} disabled={!editable} />
        </div>
        <div className="space-y-1.5">
          <Label>Responsable</Label>
          <SelectorMultiple value={filterValue('assignee')} onChange={(v) => editable && setFilter('assignee', v)} options={[...(me ? [{ value: me.userId, label: 'Yo' }] : []), ...personas.filter((p) => p.id !== me?.userId).map((p) => ({ value: p.id, label: p.nombre }))]} placeholder="Todos" disabled={!editable} />
        </div>
        {teamOptions.length > 0 && (
          <div className="space-y-1.5">
            <Label>{t('team')}</Label>
            <SelectorMultiple value={filterValue('team')} onChange={(v) => editable && setFilter('team', v)} options={teamOptions} placeholder="Todos" disabled={!editable} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Prioridad</Label>
          <SelectorMultiple value={filterValue('priority')} onChange={(v) => editable && setFilter('priority', v)} options={Object.entries(PRIORITY_LABEL).map(([value, label]) => ({ value, label }))} placeholder="Todas" disabled={!editable} />
        </div>
        {project && project.labels.length > 0 && (
          <div className="space-y-1.5">
            <Label>Etiquetas</Label>
            <SelectorMultiple value={filterValue('label')} onChange={(v) => editable && setFilter('label', v)} options={project.labels.map((l) => ({ value: l.id, label: l.name }))} placeholder="Todas" disabled={!editable} />
          </div>
        )}
        <div className="flex items-center justify-between">
          <Label htmlFor="sc">Mostrar completadas</Label>
          <Switch id="sc" checked={cfg.showCompleted !== false} onCheckedChange={(v) => editable && save({ showCompleted: v })} disabled={!editable} />
        </div>
        <div className="space-y-1.5">
          <Label>Ordenar por</Label>
          <SelectorSimple size="sm" value={cfg.sort[0]?.field ?? null} onChange={(v) => editable && save({ sort: v ? [{ field: v, dir: 'asc' }] : [] })} limpiable textoSinValor="Orden manual" options={[{ value: 'dueDate', label: 'Vencimiento' }, { value: 'priority', label: 'Prioridad' }, { value: 'title', label: 'Título' }, { value: 'createdAt', label: 'Creación' }]} disabled={!editable} />
        </div>
        {active > 0 && editable && <Button variant="ghost" size="sm" onClick={() => save({ filters: [], showCompleted: true })}>Quitar filtros</Button>}
      </PopoverContent>
    </Popover>
  );
}
